import { ageOf, createLogicGame, ensureAdultCareer, LOGIC_SCHEMA_VERSION } from "./engine.ts";
import { TEN_GENERATION_CONTENT } from "./contentPack.ts";
import type { LogicGameState } from "./types.ts";

const APTITUDE_DOMAINS = ["science", "language", "business", "creative", "sports", "care", "practical", "nature"] as const;
const EDUCATION_EVENT_BY_STAGE: Record<string, string> = {
  kindergarten: "kindergarten-choice",
  elementary: "elementary-choice",
  junior: "junior-choice",
  senior: "senior-choice",
  university: "university-choice",
  vocational: "university-choice",
  graduate: "graduate-study-choice",
};

function canonicalFamilyEventId(eventId: string) {
  return eventId.replace(/^(relative-family-.+)-[1-5]$/, "$1");
}

function consolidateFamilyEventLedger(state: LogicGameState) {
  const occurrences: LogicGameState["eventLedger"]["occurrences"] = {};
  for (const [key, occurrence] of Object.entries(state.eventLedger.occurrences || {})) {
    const separator = key.indexOf(":");
    if (separator < 0) continue;
    const oldOwner = key.slice(0, separator);
    const oldEventId = key.slice(separator + 1);
    const eventId = canonicalFamilyEventId(oldEventId);
    const person = state.people[oldOwner];
    const owner = eventId.startsWith("relative-family-") && person
      ? `household-${person.householdId}`
      : oldOwner;
    const migratedKey = `${owner}:${eventId}`;
    const previous = occurrences[migratedKey];
    occurrences[migratedKey] = {
      count: Math.min(2, Number(previous?.count || 0) + Number(occurrence.count || 0)),
      lastYear: Math.max(Number(previous?.lastYear || 0), Number(occurrence.lastYear || 0)),
    };
  }
  state.eventLedger.occurrences = occurrences;

  const topics: LogicGameState["eventLedger"]["topicLastYear"] = {};
  for (const [key, year] of Object.entries(state.eventLedger.topicLastYear || {})) {
    const separator = key.indexOf(":");
    const oldOwner = separator >= 0 ? key.slice(0, separator) : key;
    const topic = separator >= 0 ? key.slice(separator + 1) : "";
    const person = state.people[oldOwner];
    const owner = topic.startsWith("family-") && person ? `household-${person.householdId}` : oldOwner;
    const migratedKey = `${owner}:${topic}`;
    topics[migratedKey] = Math.max(Number(topics[migratedKey] || 0), Number(year || 0));
  }
  state.eventLedger.topicLastYear = topics;
}

function migratedTalent(personId: string, domain: string) {
  let hash = 2166136261;
  for (const character of `${personId}:${domain}`) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return 28 + (Math.abs(hash) % 61);
}

function recordMigratedOccurrence(state: LogicGameState, personId: string, eventId: string, year: number) {
  const key = `${personId}:${eventId}`;
  const previous = state.eventLedger.occurrences[key];
  state.eventLedger.occurrences[key] = {
    count: Math.max(1, Number(previous?.count || 0)),
    lastYear: Math.max(year, Number(previous?.lastYear || 0)),
  };
}

function repairPersonalProgress(state: LogicGameState) {
  for (const person of Object.values(state.people)) {
    person.education ||= [];
    person.flags ||= [];
    person.traits ||= [];
    for (const record of person.education) {
      const eventId = EDUCATION_EVENT_BY_STAGE[record.stage];
      if (eventId) recordMigratedOccurrence(state, person.id, eventId, record.year);
    }

    const age = ageOf(person, state.world.year);
    const educationUntil = Number(person.longTermStates.educationUntil || 0);
    const retiredWithoutCareer = person.career.status === "retired" && /學生|成長中/.test(person.career.occupation);
    if (retiredWithoutCareer) person.career.status = "student";
    const repaired = age >= 25 && educationUntil <= state.world.year
      ? ensureAdultCareer(state, person, { recordLog: false })
      : false;
    if (retiredWithoutCareer && repaired) {
      person.career.status = "retired";
      person.career.annualIncome = Math.round(person.career.annualIncome * 0.35);
    }
    if (repaired) {
      if (person.flags.includes("education-graduate")) {
        recordMigratedOccurrence(state, person.id, "career-entry-after-study", state.world.year);
      }
    }
  }

  const pending = state.pendingChoice;
  if (!pending) return;
  const event = TEN_GENERATION_CONTENT.events.find((candidate) => candidate.id === pending.eventId);
  const subjectId = pending.targetPersonId && !event?.targetRelation && !event?.targetRelations?.length
    ? pending.targetPersonId
    : pending.actorId;
  if (event?.scope === "person" && state.eventLedger.occurrences[`${subjectId}:${event.id}`]) {
    state.pendingChoice = null;
  }
}

export function serializeLogicState(state: LogicGameState) {
  return JSON.stringify(state);
}

export function migrateLogicState(value: unknown): LogicGameState {
  if (!value || typeof value !== "object") return createLogicGame("migration-fallback");
  const source = structuredClone(value) as Partial<LogicGameState> & { schemaVersion?: number };
  const fallback = createLogicGame(source.rng?.seed || "legacy-logic-save");
  if (source.people && source.households && source.activePersonId && source.activeHouseholdId) {
    const migrated = {
      ...fallback,
      ...source,
      schemaVersion: LOGIC_SCHEMA_VERSION,
      saveNamespace: "ten-generation-life-unified",
      eventLedger: source.eventLedger || fallback.eventLedger,
      pendingChoice: source.pendingChoice || null,
      annualFlow: source.annualFlow || (source.pendingChoice ? { year: source.pendingChoice.createdYear, randomEventComplete: true } : null),
      scheduledConsequences: source.scheduledConsequences || [],
    } as LogicGameState;
    migrated.newbornIdsAwaitingName = source.newbornIdsAwaitingName || [];
    Object.values(migrated.people).forEach((person) => {
      const livingPartner = person.partnerIds.map((id) => migrated.people[id]).find((partner) => partner && !partner.deathYear);
      person.relationshipStage = person.relationshipStage || (livingPartner ? "married" : "single");
      if (!person.currentPartnerId && livingPartner) person.currentPartnerId = livingPartner.id;
      person.longTermStates = person.longTermStates || {};
      person.talents ||= Object.fromEntries(APTITUDE_DOMAINS.map((domain) => [domain, migratedTalent(person.id, domain)]));
      person.discoveredTalents ||= [];
      person.interests ||= [];
    });
    Object.values(migrated.households).forEach((household) => {
      household.supportShares = {};
      if (household.id.startsWith("household-dating") && household.assets < 0) household.assets = 0;
      if (household.pregnancy) {
        household.pregnancy.decisionPending ??= false;
        household.pregnancy.protectedLeave ??= true;
      }
    });
    if (migrated.annualFlow) migrated.annualFlow.marriageBonusChecked ??= false;
    consolidateFamilyEventLedger(migrated);
    if (Number(source.schemaVersion || 0) < LOGIC_SCHEMA_VERSION) repairPersonalProgress(migrated);
    const pending = migrated.pendingChoice;
    if (pending) {
      const canonicalEventId = canonicalFamilyEventId(pending.eventId);
      if (canonicalEventId !== pending.eventId) {
        pending.eventId = canonicalEventId;
        pending.validChoiceIds = pending.validChoiceIds.map((id) => id.replace(/-[1-5]$/, ""));
      }
    }
    if (pending?.targetPersonId && !migrated.people[pending.targetPersonId]) delete pending.targetPersonId;
    if (migrated.pendingChoice && !TEN_GENERATION_CONTENT.events.some((event) => event.id === migrated.pendingChoice?.eventId)) {
      migrated.pendingChoice = null;
    }
    if (migrated.pendingChoice?.eventId === "pregnancy-decision") {
      const event = TEN_GENERATION_CONTENT.events.find((candidate) => candidate.id === "pregnancy-decision");
      migrated.pendingChoice.validChoiceIds = event?.choices.map((choice) => choice.id) || [];
    }
    if (pending && !pending.targetPersonId) {
      const event = TEN_GENERATION_CONTENT.events.find((candidate) => candidate.id === pending.eventId);
      const actor = migrated.people[pending.actorId];
      if (event?.targetRelation && actor) {
        const related = event.targetRelation === "parent" ? actor.parentIds
          : event.targetRelation === "partner" ? actor.partnerIds
            : actor.childIds;
        pending.targetPersonId = related.map((id) => migrated.people[id]).find((person) => {
          if (!person || person.deathYear || person.householdId !== pending.householdId) return false;
          const age = migrated.world.year - person.birthYear;
          return (event.targetConditions?.minAge === undefined || age >= event.targetConditions.minAge)
            && (event.targetConditions?.maxAge === undefined || age <= event.targetConditions.maxAge);
        })?.id;
        if (!pending.targetPersonId) migrated.pendingChoice = null;
      }
    }
    return migrated;
  }
  return fallback;
}

export function deserializeLogicState(serialized: string) {
  return migrateLogicState(JSON.parse(serialized));
}
